import type {
  Baseline,
  ExpressionValue,
  IElement,
  OneOfElements,
  OneOfChildElements,
  OneOfParentElements,
  Project,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { autoUpdateElementQuantity } from '../auto-quantity.helpers';
import { applyElementCategoryProperties } from '../element-category.helpers';
import { elementHasMassRelatedQuantityExpressions } from '../element-quantity.helpers';
import { syncElementExpressionResolvedFromVariables } from '../element-property.helpers';
import { handleExpressionError, solve } from '../expression-solving.helpers';
import {
  type ExpressionVariables,
  type ExpressionVariablesRecord,
  getExpressionVariablesRecordForRecipeEnrich,
  getElementExpressionVariables,
  hasQuantityEnrichTargets,
} from '../expression-variables.helpers';
import { getDisplayNumber } from '../display/get-display-number.helper';
import { getVariablesInExpression } from '../mathjs';
import { guards } from '../typeguards.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import {
  ensureDeterministicRecipeSubtree,
  isRecipeScopedElementId,
} from '../recipe.helpers';
import { resolveVersionForProposal } from './resolve-version-for-proposal.helpers';
import { createProjectShellForVersion } from '../project-factory.helpers';
import {
  forEachElement,
  getElementById,
  getPathToElement,
  isElement,
} from '../recursive-element.helpers';
import { getAffectedElementIds } from '../actions/action.helpers';
import { getProposal } from '../proposals/proposals.helpers';
import {
  normalizeResolvedVersionEnrichContext,
  type ResolvedVersionEnrichContext,
} from './resolved-version-enrich-cache.helpers';
import { getEmptyFrozenArray } from '../array.helpers';
import { genericProductsLookup } from '@shared/generic-products';
import type { IDataTable } from '@shared/models/data-table.model';
import type { ProductRecord } from '@shared/models/product.model';
import { ConcretePropertyName } from '@shared/templates/categories/concrete/concrete.model';

/**
 * Element ids that need category / recipe-quantity re-enrichment for a proposal:
 * action targets, their ancestors, and descendants on the resolved tree.
 */
const collectProposalEnrichScopeElementIds = (
  resolvedVersion: Baseline,
  baselineVersion: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
): ReadonlySet<string> | undefined => {
  const proposal = getProposal(baselineVersion, proposalOrId);
  if (!proposal?.actionIds.length || !baselineVersion.actions?.length) {
    return undefined;
  }

  const actionIdSet = new Set(proposal.actionIds);
  const scope = new Set<string>();

  for (const action of baselineVersion.actions) {
    if (!actionIdSet.has(action.id)) continue;
    for (const targetId of getAffectedElementIds(action.changes)) {
      scope.add(targetId);
      const path = getPathToElement(baselineVersion, targetId, false);
      if (path) {
        for (const ancestor of path) scope.add(ancestor.id);
      }
      const targetOnResolved = getElementById(resolvedVersion, targetId, false);
      if (targetOnResolved && isElement(targetOnResolved)) {
        forEachElement(targetOnResolved, (el) => scope.add(el.id));
      }
    }
  }

  return scope.size > 0 ? scope : undefined;
};

/**
 * One post-order pass: ids whose subtree intersects {@link scopeIds} (node in scope,
 * descendant in scope, or ancestor path to a scoped node).
 */
const buildEnrichBranchMembership = (
  version: Baseline,
  scopeIds: ReadonlySet<string>,
): ReadonlySet<string> => {
  const membership = new Set<string>();

  const visit = (el: OneOfElements): boolean => {
    let childTouches = false;
    if (projectGuards.is.element(el)) {
      for (const child of el.elements) {
        if (visit(child as OneOfElements)) childTouches = true;
      }
    }
    if (scopeIds.has(el.id) || childTouches) {
      membership.add(el.id);
      return true;
    }
    return false;
  };

  for (const root of version.elements) visit(root);
  return membership;
};

/**
 * Re-runs {@link applyElementCategoryProperties} on every IElement in the tree so
 * category-driven children (e.g. metal `form`, heating `type`) reflect property
 * values from the active proposal — mirrors what baseline enrichment writes back
 * to the version tree in {@link enrichFromComputeRecord}.
 *
 * When {@link scopeIds} is set, only branches that touch the scope are enriched (DEV-3293 perf).
 *
 * Per-element id stability: {@link applyCategoryChildElements} reuses
 * `element.elements[i].id` for the slot at the same index, so productElement.id
 * survives a property-driven product_id / unit / count change.
 */
const enrichResolvedTreeCategoryOnly = (
  version: Baseline,
  enrichBranches?: ReadonlySet<string>,
): Baseline => {
  const enrichChildren = (
    children: OneOfChildElements[],
  ): OneOfChildElements[] => {
    let changed = false;
    const next = children.map((child) => {
      if (enrichBranches && !enrichBranches.has(child.id)) return child;
      const enriched = enrichElement(child);
      if (enriched !== child) changed = true;
      return enriched as OneOfChildElements;
    });
    return changed ? next : children;
  };

  // Bottom-up so a parent's category template sees up-to-date children before its own enrichment runs
  const enrichElement = (el: OneOfElements): OneOfElements => {
    if (!projectGuards.is.element(el)) return el;
    if (enrichBranches && !enrichBranches.has(el.id)) return el;

    const childrenEnriched = enrichChildren(el.elements);
    const withEnrichedChildren =
      childrenEnriched === el.elements
        ? el
        : { ...el, elements: childrenEnriched };
    const categoryEnriched =
      applyElementCategoryProperties(withEnrichedChildren);
    if (!projectGuards.is.element(categoryEnriched)) return categoryEnriched;
    if (!isRecipeScopedElementId(categoryEnriched.id)) return categoryEnriched;
    return ensureDeterministicRecipeSubtree(categoryEnriched);
  };

  const nextElements = enrichChildren(version.elements);
  return nextElements === version.elements
    ? version
    : { ...version, elements: nextElements };
};

/**
 * Categories whose quantity template includes mass or areal density (kg/m²).
 * Wrapper rows like {@link ElementCategoryID.Other} only have geometry quantities — skip them (DEV-3349).
 */
const elementNeedsMassAutoQuantity = (element: IElement): boolean => {
  if (isRecipeScopedElementId(element.id)) return true;
  return elementHasMassRelatedQuantityExpressions(element);
};

/**
 * True when the formula uses reinforcement and this variable record has none.
 * Leaving cast-in-place removes that property. Other missing symbols still
 * throw inside {@link solveProductCount} and keep the previous amount (DEV-3915).
 */
const expressionMissesReinforcement = (
  expression: string,
  variables: ExpressionVariables,
): boolean => {
  const scope: Record<string, unknown> = variables;
  const name = ConcretePropertyName.ReinforcementSteel;
  return (
    getVariablesInExpression(expression).includes(name) &&
    !guards.is.defined(scope[name])
  );
};

/**
 * Solve a generated product count from the parent variable bag.
 * A thrown formula keeps the previous count. Zeroing every failure would make
 * the side panel price that zero.
 * A successful solve of 0 is returned.
 * @param count - Product count expression to solve
 * @param variables - Parent element variables
 */
const solveProductCount = (
  count: ExpressionValue,
  variables: ExpressionVariables,
): ExpressionValue | undefined => {
  try {
    return solve(count.value, count.expression, variables);
  } catch (error) {
    handleExpressionError(error);
    return undefined;
  }
};

/**
 * Write solved product counts onto generated children.
 *
 * Category enrichment keeps the previous `count.resolved` when it rewrites the
 * formula, for example cast-in-place concrete becoming hollow core. The climate
 * panel multiplies that stale count (DEV-3915, DEV-3919).
 *
 * Missing reinforcement becomes 0 so the leftover steel block hides. Any other
 * missing symbol or thrown formula keeps the previous amount (DEV-3915).
 *
 * @param element - Parent whose product children should follow {@link variables}
 * @param variables - Solved symbols for {@link element}, including mass and volume
 */
const syncProductElementCountsFromVariables = (
  element: IElement,
  variables: ExpressionVariables,
): IElement => {
  let changed = false;
  const elements = element.elements.map((child) => {
    if (!projectGuards.is.productElement(child)) return child;
    const count = child.count;
    if (!count?.expression) return child;

    // A typed amount wins over the formula, same as {@link solve}.
    if (
      !guards.is.number(count.value) &&
      expressionMissesReinforcement(count.expression, variables)
    ) {
      if (count.resolved === 0) return child;
      changed = true;
      return {
        ...child,
        count: {
          ...count,
          resolved: 0,
          formatted: getDisplayNumber(0),
        },
      };
    }

    const solved = solveProductCount(count, variables);
    if (!solved || solved.resolved === count.resolved) return child;

    changed = true;
    return { ...child, count: solved };
  });

  return changed ? { ...element, elements } : element;
};

/**
 * Re-run auto-quantity on proposal-scoped branches (recipe children and quantity-bearing elements).
 * Recipe rows may keep frozen `fallbackCount.expression: "0"` from the action snapshot (DEV-3293).
 * Other-product rows need the same when quantity overrides exist only on the proposal (DEV-3349).
 */
const enrichBranchQuantities = (
  children: OneOfChildElements[],
  path: OneOfParentElements[],
  variablesRecord: ExpressionVariablesRecord,
  productRecord: ProductRecord,
  dataTables: IDataTable[],
  enrichBranches?: ReadonlySet<string>,
): { children: OneOfChildElements[]; changed: boolean } => {
  let changed = false;
  const next = children.map((child) => {
    if (!projectGuards.is.element(child)) return child;
    if (enrichBranches && !enrichBranches.has(child.id)) return child;

    let updated: IElement = child;
    const needsAutoQuantity = elementNeedsMassAutoQuantity(child);
    if (needsAutoQuantity) {
      const withAutoQty = autoUpdateElementQuantity(
        child,
        path,
        variablesRecord,
        {
          cacheNamespace: 'proposal-enrich',
          forceRecompute: !!enrichBranches?.has(child.id),
        },
      );
      // Auto-quantity rewrites frozen "0" fallbacks to getParentValue after the
      // variable record was built. Re-solve this element before writing resolved
      // numbers, or parent area and reinforcement stay at the placeholder.
      const parentElement = path[path.length - 1];
      const elementVars = getElementExpressionVariables(
        withAutoQty,
        parentElement ? variablesRecord[parentElement.id] : undefined,
        productRecord,
        dataTables,
      );
      variablesRecord[withAutoQty.id] = elementVars;
      const withResolved = syncProductElementCountsFromVariables(
        syncElementExpressionResolvedFromVariables(withAutoQty, elementVars),
        elementVars,
      );
      if (withResolved !== child) {
        updated = withResolved;
        changed = true;
      }
    }

    const childPath: OneOfParentElements[] = [...path, updated];
    const nested = enrichBranchQuantities(
      updated.elements,
      childPath,
      variablesRecord,
      productRecord,
      dataTables,
      enrichBranches,
    );
    if (nested.changed) {
      updated = { ...updated, elements: nested.children };
      changed = true;
    }
    return updated;
  });

  return { children: changed ? next : children, changed };
};

/**
 * Re-run category enrichment and, when needed, auto-quantity on proposal quantity targets.
 *
 * Perf: category-only path when {@link hasQuantityEnrichTargets} is false; expression variables are
 * loaded via {@link getExpressionVariablesRecordForRecipeEnrich} (shared cache or narrow uncached build).
 */
export const enrichResolvedTree = (
  version: Baseline,
  project?: Project,
  scopeIds?: ReadonlySet<string>,
  proposalOrId?: ItemOrItemId<IProposal> | undefined,
  enrichInputs?: Pick<
    ResolvedVersionEnrichContext,
    'weatherHours' | 'dataTables'
  >,
): Baseline => {
  const enrichBranches = scopeIds
    ? buildEnrichBranchMembership(version, scopeIds)
    : undefined;
  const categoryEnriched = enrichResolvedTreeCategoryOnly(
    version,
    enrichBranches,
  );

  // Property-only proposals: category enrich is enough; skip ~2s full-tree expression variables.
  if (!hasQuantityEnrichTargets(categoryEnriched, enrichBranches)) {
    return categoryEnriched;
  }

  const proposal =
    project && proposalOrId ? getProposal(version, proposalOrId) : undefined;
  const enrichProject =
    project ?? createProjectShellForVersion(categoryEnriched);
  const dataTables = enrichInputs?.dataTables ?? getEmptyFrozenArray();
  const variablesRecord: ExpressionVariablesRecord =
    getExpressionVariablesRecordForRecipeEnrich(
      enrichProject,
      categoryEnriched,
      {
        proposal,
        enrichBranches,
        weatherHours: enrichInputs?.weatherHours ?? getEmptyFrozenArray(),
        dataTables,
      },
    );
  const productRecord: ProductRecord = {
    ...genericProductsLookup,
    ...categoryEnriched.products,
  };
  const { children, changed } = enrichBranchQuantities(
    categoryEnriched.elements,
    [categoryEnriched],
    variablesRecord,
    productRecord,
    dataTables,
    enrichBranches,
  );
  if (!changed) return categoryEnriched;
  return { ...categoryEnriched, elements: children };
};

/**
 * Apply proposal actions, then re-run category enrichment on the resolved tree.
 */
export const resolveAndEnrichVersionForProposal = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
  enrichContext?: ResolvedVersionEnrichContext | Project,
): Baseline => {
  const resolved = resolveVersionForProposal(version, proposalOrId).version;
  if (resolved === version) return resolved;

  const normalizedContext =
    normalizeResolvedVersionEnrichContext(enrichContext);
  const scopeIds = collectProposalEnrichScopeElementIds(
    resolved,
    version,
    proposalOrId,
  );
  return enrichResolvedTree(
    resolved,
    normalizedContext?.project,
    scopeIds,
    proposalOrId,
    normalizedContext,
  );
};
